import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchSubmission } from './api';
import { applyBranding } from './theme';
import { BrandProvider } from './components/BrandContext';
import { PhaseProvider } from './components/PhaseContext';
import type { Assessment, SubmissionScores, Answers, PageStyle } from './types';
import Result from './screens/Result';
import BrokenPage from './components/BrokenPage';

interface LoadedState {
  assessment: Assessment;
  scores: SubmissionScores;
  submissionId: string;
  resultKey: string;
  answers: Answers;
}

export default function ResultsPage() {
  const { submissionId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'error'; error: string }
    | ({ phase: 'ready' } & LoadedState)
  >({ phase: 'loading' });

  useEffect(() => {
    let alive = true;
    fetchSubmission(submissionId)
      .then((data) => {
        if (!alive) return;
        setState({
          phase: 'ready',
          assessment: data.assessment,
          scores: data.scores,
          submissionId: data.submissionId,
          resultKey: data.resultKey,
          answers: data.answers,
        });
      })
      .catch((e) => { if (alive) setState({ phase: 'error', error: e.message }); });
    return () => { alive = false; };
  }, [submissionId]);

  useEffect(() => {
    if (state.phase === 'ready') {
      applyBranding(state.assessment.config.branding, { brand: state.assessment.brand ?? null });
      document.title = `${state.assessment.title} — Your Results`;
    }
  }, [state]);

  // Page-level background-image override from result copy's pageStyle. Body bg
  // color/gradient are theme-owned (see App.tsx pageStyle effect comment) — we
  // only honor backgroundImageUrl here.
  useEffect(() => {
    if (state.phase !== 'ready') return;
    const resultCopy = state.assessment.config.results?.[state.resultKey];
    const pageStyle: PageStyle | undefined = resultCopy?.pageStyle;
    const body = document.body;
    if (pageStyle?.backgroundImageUrl && !pageStyle.backgroundGradient) {
      body.style.backgroundImage = `url("${pageStyle.backgroundImageUrl}")`;
    }
    return () => {
      body.style.backgroundImage = '';
    };
  }, [state]);

  if (state.phase === 'loading') {
    return <div className="loading-screen">Loading your results…</div>;
  }
  if (state.phase === 'error') {
    const isNotFound = /not found|404/i.test(state.error || '');
    return (
      <BrokenPage
        variant={isNotFound ? '404' : 'error'}
        message={isNotFound
          ? "We couldn't find those results. If you came from an email, the link may have expired or been copied incorrectly. Tell us which email you got it in and we'll resend it."
          : undefined}
      />
    );
  }

  const { assessment, scores, submissionId: sid, resultKey, answers } = state;
  // Ensure scores.result is set — older submissions may have it as null
  const patchedScores = scores.result ? scores : { ...scores, result: resultKey };
  const branding = assessment.config.branding;
  const effectiveLogoUrl = assessment.brand?.logoUrl ?? branding?.logoUrl ?? null;
  const logoLinkUrl = assessment.brand?.logoLinkUrl ?? null;
  const showLogo = assessment.config.intro.showLogo !== false && !!effectiveLogoUrl;

  return (
    <BrandProvider brand={assessment.brand ?? null}>
    <PhaseProvider phase="result">
    <div className="app-shell">
      {showLogo && (
        <div className="brand-bar">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} className="brand-bar-link" aria-label={assessment.brand?.name ?? 'Home'}>
              <img src={effectiveLogoUrl!} alt={assessment.brand?.name ?? ''} />
            </a>
          ) : (
            <img src={effectiveLogoUrl!} alt={assessment.brand?.name ?? ''} />
          )}
        </div>
      )}
      <Result assessment={assessment} scores={patchedScores} submissionId={sid} answers={answers} preview={isPreview} />
    </div>
    </PhaseProvider>
    </BrandProvider>
  );
}
